import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDemo } from '../contexts/DemoContext';
import { buildApiUrl } from '../config/api';

export const useAssets = () => {
  const { getAuthHeaders, user } = useAuth();
  const { isDemoModeActive, getDemoQueryParams } = useDemo();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch all assets
  const fetchAssets = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      const queryParams = getDemoQueryParams();
      const response = await fetch(buildApiUrl(`/api/asset${queryParams}`), {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      const result = await response.json();

      if (result.success) {
        setAssets(result.data || []);
      } else {
        setError(result.error || 'Failed to fetch assets');
      }
    } catch (err) {
      console.error('Error fetching assets:', err);
      setError('Failed to fetch assets');
    } finally {
      setLoading(false);
    }
  }, [user, getAuthHeaders, getDemoQueryParams]);

  // Create new asset
  const createAsset = useCallback(async (assetData) => {
    if (!user) throw new Error('No authentication token');
    if (isDemoModeActive) throw new Error('Cannot modify demo data. Turn off demo mode to edit your data.');

    try {
      const response = await fetch(buildApiUrl('/api/asset'), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(assetData),
      });

      const result = await response.json();

      if (result.success) {
        await fetchAssets(); // Refresh the list
        return result.data;
      } else {
        throw new Error(result.error || 'Failed to create asset');
      }
    } catch (err) {
      console.error('Error creating asset:', err);
      throw err;
    }
  }, [user, getAuthHeaders, fetchAssets, isDemoModeActive]);

  // Update existing asset
  const updateAsset = useCallback(async (id, assetData) => {
    if (!user) throw new Error('No authentication token');
    if (isDemoModeActive) throw new Error('Cannot modify demo data. Turn off demo mode to edit your data.');

    try {
      console.log('useAssets: Updating asset with ID:', id);
      console.log('useAssets: Update data:', assetData);
      
      const response = await fetch(buildApiUrl(`/api/asset/${id}`), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(assetData),
      });

      const result = await response.json();

      if (result.success) {
        await fetchAssets(); // Refresh the list
        return result.data;
      } else {
        throw new Error(result.error || 'Failed to update asset');
      }
    } catch (err) {
      console.error('Error updating asset:', err);
      throw err;
    }
  }, [user, getAuthHeaders, fetchAssets, isDemoModeActive]);

  // Delete asset
  const deleteAsset = useCallback(async (id) => {
    if (!user) throw new Error('No authentication token');
    if (isDemoModeActive) throw new Error('Cannot modify demo data. Turn off demo mode to edit your data.');

    try {
      console.log('useAssets: Deleting asset with ID:', id);
      
      const response = await fetch(buildApiUrl(`/api/asset/${id}`), {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      const result = await response.json();

      if (result.success) {
        await fetchAssets(); // Refresh the list
        return true;
      } else {
        throw new Error(result.error || 'Failed to delete asset');
      }
    } catch (err) {
      console.error('Error deleting asset:', err);
      throw err;
    }
  }, [user, getAuthHeaders, fetchAssets, isDemoModeActive]);

  // Get assets by type
  const getAssetsByType = useCallback(async (type) => {
    if (!user) return [];

    try {
      const queryParams = getDemoQueryParams();
      const response = await fetch(buildApiUrl(`/api/asset/type/${encodeURIComponent(type)}${queryParams}`), {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      const result = await response.json();

      if (result.success) {
        return result.data || [];
      } else {
        throw new Error(result.error || 'Failed to fetch assets by type');
      }
    } catch (err) {
      console.error('Error fetching assets by type:', err);
      throw err;
    }
  }, [user, getAuthHeaders, getDemoQueryParams]);

  // Calculate summary statistics
  const calculateSummary = useCallback(() => {
    const totalValue = assets.reduce((sum, asset) => sum + (asset.currentValue || 0), 0);
    const totalCost = assets.reduce((sum, asset) => sum + (asset.purchaseValue || asset.currentValue || 0), 0);
    const totalGain = totalValue - totalCost;
    const gainPercentage = totalCost > 0 ? (totalGain / totalCost) * 100 : 0;
    
    const liquidAssets = assets.filter(asset => asset.liquidity === 'Liquid');
    const liquidValue = liquidAssets.reduce((sum, asset) => sum + (asset.currentValue || 0), 0);
    const liquidPercentage = totalValue > 0 ? (liquidValue / totalValue) * 100 : 0;

    const projectedValue = assets.reduce((sum, asset) => {
      const years = 1;
      const futureValue = (asset.currentValue || 0) * Math.pow(1 + ((asset.expectedReturn || 0) / 100), years);
      return sum + futureValue;
    }, 0);
    
    const projectedGain = projectedValue - totalValue;
    const projectedGainPercentage = totalValue > 0 ? (projectedGain / totalValue) * 100 : 0;

    const assetsByType = assets.reduce((acc, asset) => {
      const type = asset.type || 'Other';
      if (!acc[type]) acc[type] = [];
      acc[type].push(asset);
      return acc;
    }, {});

    return {
      totalAssets: assets.length,
      totalValue,
      totalCost,
      totalGain,
      gainPercentage,
      liquidValue,
      liquidPercentage,
      projectedValue,
      projectedGain,
      projectedGainPercentage,
      assetsByType,
      types: Object.keys(assetsByType)
    };
  }, [assets]);

  // Load assets on mount and when user changes
  useEffect(() => {
    if (user) {
      fetchAssets();
    } else {
      setAssets([]);
      setLoading(false);
    }
  }, [user, fetchAssets]);

  return {
    assets,
    loading,
    error,
    createAsset,
    updateAsset,
    deleteAsset,
    getAssetsByType,
    calculateSummary,
    refreshAssets: fetchAssets,
  };
};
